import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  set,
  startOfDay,
} from "date-fns";
import { ApolloError, NetworkStatus } from "@apollo/client";
import {
  BookingData,
  BookingTool,
  ServiceDuration,
} from "../../../AppDataProvider/AppDataProvider";
import {
  getAvailabilityPartsInput,
  requireBookingDataFields,
} from "../../../utils";

import { PartRequired } from "../../../utils/types";
import { today } from "../../../utils";
import { useGetOrganisationAvailabilityDatesQuery } from "../../../GeneratedGraphQL";

export type UseGetAvailableDatesOpts = {
  attendees: number;
  availabilityMonthOffset: number;
  bookingData: BookingData;
  bookingTool: BookingTool;
  duration: ServiceDuration;
};

export type UseGetAvailableDatesResult = {
  availableDates: Date[];
  error?: ApolloError;
  getMoreAvailability: (options: GetMoreAvailabilityOpts) => Promise<void>;
  loading: boolean;
  networkStatus: NetworkStatus;
};

type GetMoreAvailabilityOpts = {
  monthOffset: number;
  setAvailabilityMonthOffset: (monthOffset: number) => void;
  setMonthOffset: (monthOffset: number) => void;
};

type UseGetAvailableDatesBookingData = PartRequired<
  BookingData,
  "organisation" | "service" | "sessionId" | "timezone"
>;

export const useGetAvailableDates = (
  options: UseGetAvailableDatesOpts,
): UseGetAvailableDatesResult => {
  const {
    attendees,
    duration,
    availabilityMonthOffset,
    bookingData,
    bookingTool,
  } = options;

  requireBookingDataFields<UseGetAvailableDatesBookingData>(bookingData, [
    "service",
    "organisation",
    "sessionId",
    "timezone",
  ]);

  const {
    organisation,
    service,
    resource,
    resourceGroupId,
    timezone,
    sessionId,
  } = bookingData;
  const { parts } = service;
  const partsInput = getAvailabilityPartsInput(parts);

  const currentMonthDate = addMonths(today, availabilityMonthOffset);

  const rangeStartDate = set(currentMonthDate, {
    date: availabilityMonthOffset > 0 ? 1 : new Date(today).getDate(),
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const rangeEndDate = startOfDay(addDays(rangeStartDate, 42));

  /**
   * If we have been provided a resourceId in the url params,
   * we want to automatically filter availability by that resource id.
   */
  const urlParams = new URLSearchParams(window.location.search);
  const preFillResourceId = urlParams.get("resourceId");

  const resourceId = resource?._id ?? preFillResourceId;

  const requestVariables = {
    ...(!bookingTool.isGenericBookingTool && {
      bookingToolId: bookingTool._id,
    }),
    duration: duration.minutes,
    id: organisation._id,
    meta: { sessionId },
    ...(partsInput.length > 0 && { parts: partsInput }),
    serviceId: service._id,
    ...(resourceId && { resourceIds: [resourceId] }),
    ...(resourceGroupId && { resourceGroupIds: [resourceGroupId] }),
    ranges: [
      {
        end: rangeEndDate.toISOString(),
        start: rangeStartDate.toISOString(),
      },
    ],
    spaces: attendees,
    timezone,
  };

  const { data, loading, error, networkStatus, fetchMore } =
    useGetOrganisationAvailabilityDatesQuery({
      fetchPolicy: "network-only",
      nextFetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      variables: requestVariables,
    });

  const availableDates =
    (data?.organisation?.availabilityDates &&
      data?.organisation?.availabilityDates.map((availableDate) => {
        return new Date(availableDate.date);
      })) ??
    [];

  /**
   * Get more availability than what is currently shown on the
   * calendar view by searching 6 months ahead of the current
   * monthOffset in 6 monthly intervals.
   */
  const getMoreAvailability = async (options: GetMoreAvailabilityOpts) => {
    const { monthOffset, setMonthOffset, setAvailabilityMonthOffset } = options;

    const ranges = [...new Array(6).keys()].map((count) => {
      /**
       * - Raw 1 value
       * Ignores the current month that returned no availability.
       *
       * - count
       * The range iteration we are in.
       *
       * - monthOffset
       * The based count of months we are searching
       * relative to the current date.
       */
      const baseDate = addMonths(today, 1 + count + monthOffset);

      const rangeStart = set(baseDate, {
        date: 1,
        hours: 0,
        minutes: 0,
        seconds: 0,
      });

      const rangeEnd = set(addMonths(baseDate, 1), {
        date: 1,
        hours: 0,
        minutes: 0,
        seconds: 0,
      });

      return {
        end: rangeEnd.toISOString(),
        start: rangeStart.toISOString(),
      };
    });

    const { data } = await fetchMore({
      variables: {
        ...requestVariables,
        ranges: ranges,
        requestRangesIndividually: true,
      },
    });

    /**
     * If we find available dates within the next 6 months
     * we want to automatically move the calendar to
     * the earliest possible date.
     *
     * Else set the currently selected date as today.
     */
    if (
      data?.organisation?.availabilityDates &&
      data?.organisation?.availabilityDates.length > 0
    ) {
      const availableDates = data?.organisation?.availabilityDates.map(
        (availableDate) => {
          return new Date(availableDate.date);
        },
      );
      const monthDifference = differenceInCalendarMonths(
        availableDates[0],
        today,
      );
      setMonthOffset(monthDifference);
      setAvailabilityMonthOffset(monthDifference);
    } else {
      setMonthOffset(monthOffset + 6);
      setAvailabilityMonthOffset(monthOffset + 6);
    }
  };

  return {
    availableDates,
    error,
    getMoreAvailability,
    loading,
    networkStatus,
  };
};
